import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Editor } from '@tiptap/react';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Quote,
  Code,
  Link as LinkIcon,
  Image as ImageIcon,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Undo,
  Redo,
  ChevronDown,
  ChevronUp,
  Strikethrough
} from 'lucide-react';

interface EditorToolbarProps {
  editor: Editor;
  disabled?: boolean;
}

interface Tool {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  icon: React.ReactNode;
  tooltip: string;
  shortcut?: string;
}

interface ToolGroup {
  group: string;
  tools: Tool[];
}

export const EditorToolbar: React.FC<EditorToolbarProps> = ({ editor, disabled = false }) => {
  const [showAllTools, setShowAllTools] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  // Index of the button that currently holds the toolbar's single tab stop.
  const [activeIndex, setActiveIndex] = useState(0);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const toolbarDisabled = disabled || !editor?.isEditable;

  useEffect(() => {
    if (toolbarDisabled) {
      setShowImageDialog(false);
      setShowLinkDialog(false);
    }
  }, [toolbarDisabled]);

  // Collapsing the mobile overflow can hide the button holding the tab stop.
  useEffect(() => {
    if (!showAllTools) {
      setActiveIndex(0);
    }
  }, [showAllTools]);

  const addImage = () => {
    if (toolbarDisabled) return;
    if (imageUrl.trim()) {
      editor.chain().focus().setImage({ src: imageUrl.trim() }).run();
      setImageUrl('');
      setShowImageDialog(false);
    }
  };

  const setLink = () => {
    if (toolbarDisabled) return;
    if (linkUrl.trim()) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl.trim() }).run();
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }
    setLinkUrl('');
    setShowLinkDialog(false);
  };

  const quickSetLink = useCallback(() => {
    if (toolbarDisabled) return;
    const previousUrl = editor.getAttributes('link').href;
    setLinkUrl(previousUrl || '');
    setShowLinkDialog(true);
  }, [editor, toolbarDisabled]);

  // Primary tools (always visible)
  const primaryTools: ToolGroup[] = useMemo(() => [
    {
      group: 'format',
      tools: [
        {
          onClick: () => editor.chain().focus().toggleBold().run(),
          active: editor.isActive('bold'),
          icon: <Bold className="w-4 h-4" />,
          tooltip: 'Bold',
          shortcut: '⌘B'
        },
        {
          onClick: () => editor.chain().focus().toggleItalic().run(),
          active: editor.isActive('italic'),
          icon: <Italic className="w-4 h-4" />,
          tooltip: 'Italic',
          shortcut: '⌘I'
        }
      ]
    },
    {
      group: 'headings',
      tools: [
        {
          onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
          active: editor.isActive('heading', { level: 1 }),
          icon: <Heading1 className="w-4 h-4" />,
          tooltip: 'Heading 1',
          shortcut: '#'
        },
        {
          onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
          active: editor.isActive('heading', { level: 2 }),
          icon: <Heading2 className="w-4 h-4" />,
          tooltip: 'Heading 2',
          shortcut: '##'
        }
      ]
    },
    {
      group: 'lists',
      tools: [
        {
          onClick: () => editor.chain().focus().toggleBulletList().run(),
          active: editor.isActive('bulletList'),
          icon: <List className="w-4 h-4" />,
          tooltip: 'Bullet List',
          shortcut: '• '
        },
        {
          onClick: () => editor.chain().focus().toggleOrderedList().run(),
          active: editor.isActive('orderedList'),
          icon: <ListOrdered className="w-4 h-4" />,
          tooltip: 'Numbered List',
          shortcut: '1. '
        }
      ]
    }
  ], [editor]);

  // Secondary tools (collapsible on mobile)
  const secondaryTools: ToolGroup[] = useMemo(() => [
    {
      group: 'advanced-format',
      tools: [
        {
          onClick: () => editor.chain().focus().toggleStrike().run(),
          active: editor.isActive('strike'),
          icon: <Strikethrough className="w-4 h-4" />,
          tooltip: 'Strikethrough',
          shortcut: '~~'
        },
        {
          onClick: () => editor.chain().focus().toggleHighlight().run(),
          active: editor.isActive('highlight'),
          icon: <Highlighter className="w-4 h-4" />,
          tooltip: 'Highlight',
          shortcut: '=='
        },
        {
          onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
          active: editor.isActive('heading', { level: 3 }),
          icon: <Heading3 className="w-4 h-4" />,
          tooltip: 'Heading 3',
          shortcut: '###'
        }
      ]
    },
    {
      group: 'blocks',
      tools: [
        {
          onClick: () => editor.chain().focus().toggleBlockquote().run(),
          active: editor.isActive('blockquote'),
          icon: <Quote className="w-4 h-4" />,
          tooltip: 'Quote',
          shortcut: '> '
        },
        {
          onClick: () => editor.chain().focus().toggleCodeBlock().run(),
          active: editor.isActive('codeBlock'),
          icon: <Code className="w-4 h-4" />,
          tooltip: 'Code Block',
          shortcut: '```'
        }
      ]
    },
    {
      group: 'media',
      tools: [
        {
          onClick: quickSetLink,
          active: editor.isActive('link'),
          icon: <LinkIcon className="w-4 h-4" />,
          tooltip: 'Add Link',
          shortcut: '⌘K'
        },
        {
          onClick: () => {
            if (toolbarDisabled) return;
            setShowImageDialog(true);
          },
          active: false,
          icon: <ImageIcon className="w-4 h-4" />,
          tooltip: 'Add Image',
          shortcut: 'Alt+I'
        }
      ]
    },
    {
      group: 'history',
      tools: [
        {
          onClick: () => editor.chain().focus().undo().run(),
          disabled: !editor.can().undo(),
          icon: <Undo className="w-4 h-4" />,
          tooltip: 'Undo',
          shortcut: '⌘Z'
        },
        {
          onClick: () => editor.chain().focus().redo().run(),
          disabled: !editor.can().redo(),
          icon: <Redo className="w-4 h-4" />,
          tooltip: 'Redo',
          shortcut: '⌘⇧Z'
        }
      ]
    }
  ], [editor, toolbarDisabled, quickSetLink]);

  const primaryCount = useMemo(
    () => primaryTools.reduce((total, group) => total + group.tools.length, 0),
    [primaryTools]
  );
  // The mobile expand/collapse control sits between the two blocks in DOM order.
  const chevronIndex = primaryCount;
  const totalButtons = useMemo(
    () => chevronIndex + 1 + secondaryTools.reduce((total, group) => total + group.tools.length, 0),
    [chevronIndex, secondaryTools]
  );

  /**
   * The toolbar is a single tab stop (WAI-ARIA toolbar pattern). If the remembered
   * button is disabled, fall back to the first enabled one so the stop is never dead.
   * Computed every render rather than memoized — it reads mutable refs, which no
   * dependency array can track.
   */
  const rememberedButton = buttonRefs.current[activeIndex];
  const firstEnabledIndex = buttonRefs.current.findIndex((el) => el && !el.disabled);
  const tabStopIndex =
    rememberedButton && !rememberedButton.disabled
      ? activeIndex
      : Math.max(firstEnabledIndex, 0);

  /** Focus the next enabled button, stepping over any that refuse focus (hidden on mobile). */
  const focusButtonFrom = useCallback((start: number, step: number) => {
    for (let attempt = 0; attempt < totalButtons; attempt += 1) {
      const index = (((start + step * attempt) % totalButtons) + totalButtons) % totalButtons;
      const candidate = buttonRefs.current[index];
      if (!candidate || candidate.disabled) continue;

      candidate.focus();
      if (document.activeElement === candidate) {
        setActiveIndex(index);
        return;
      }
    }
  }, [totalButtons]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const current = buttonRefs.current.indexOf(event.target as HTMLButtonElement);
    if (current === -1) return;

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        focusButtonFrom(current + 1, 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        focusButtonFrom(current - 1, -1);
        break;
      case 'Home':
        event.preventDefault();
        focusButtonFrom(0, 1);
        break;
      case 'End':
        event.preventDefault();
        focusButtonFrom(totalButtons - 1, -1);
        break;
      default:
        break;
    }
  }, [focusButtonFrom, totalButtons]);

  // Keep the remembered stop in sync when a button is reached by mouse or arrow keys.
  const handleFocus = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    const index = buttonRefs.current.indexOf(event.target as HTMLButtonElement);
    if (index !== -1) {
      setActiveIndex(index);
    }
  }, []);

  const registerButton = useCallback(
    (index: number) => (node: HTMLButtonElement | null) => {
      buttonRefs.current[index] = node;
    },
    []
  );

  const renderGroups = (groups: ToolGroup[], offset: number) => {
    let cursor = offset;

    return groups.map((group, groupIndex) => {
      const rendered = (
        <React.Fragment key={group.group}>
          {group.tools.map((tool, toolIndex) => {
            const index = cursor + toolIndex;
            return (
              <ToolbarButton
                key={`${group.group}-${toolIndex}`}
                ref={registerButton(index)}
                tabIndex={index === tabStopIndex ? 0 : -1}
                onClick={() => {
                  if (toolbarDisabled) return;
                  tool.onClick();
                }}
                active={tool.active}
                disabled={toolbarDisabled || tool.disabled}
                icon={tool.icon}
                tooltip={tool.tooltip}
                shortcut={tool.shortcut}
              />
            );
          })}
          {groupIndex < groups.length - 1 && <ToolbarDivider />}
        </React.Fragment>
      );

      cursor += group.tools.length;
      return rendered;
    });
  };

  return (
    <div className="relative">
      <div
        role="toolbar"
        aria-label="Text formatting"
        aria-orientation="horizontal"
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        className="flex flex-wrap items-center gap-1 p-2 bg-white border border-gray-200 rounded-xl shadow-sm"
      >
        {/* Primary Tools - Always Visible */}
        <div className="flex items-center gap-1">
          {renderGroups(primaryTools, 0)}
        </div>

        {/* Expand/Collapse Button */}
        <ToolbarDivider />
        <button
          type="button"
          ref={registerButton(chevronIndex)}
          tabIndex={chevronIndex === tabStopIndex ? 0 : -1}
          onClick={() => {
            if (toolbarDisabled) return;
            setShowAllTools(!showAllTools);
          }}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors duration-200 sm:hidden disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1"
          title={showAllTools ? 'Show Less' : 'Show More Tools'}
          aria-expanded={showAllTools}
          disabled={toolbarDisabled}
        >
          {showAllTools ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>

        {/* Secondary Tools - Desktop Always Visible, Mobile Collapsible */}
        <div className={`${showAllTools ? 'flex' : 'hidden'} sm:flex items-center gap-1 ${showAllTools ? 'w-full mt-2 pt-2 border-t border-gray-200' : ''} sm:w-auto sm:mt-0 sm:pt-0 sm:border-t-0`}>
          {renderGroups(secondaryTools, chevronIndex + 1)}
        </div>
      </div>

      {/* Image Dialog */}
      {showImageDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Image</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="imageUrl" className="block text-sm font-medium text-gray-700 mb-2">
                  Image URL
                </label>
                <input
                  id="imageUrl"
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  autoFocus
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowImageDialog(false);
                    setImageUrl('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={addImage}
                  disabled={!imageUrl.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  Add Image
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Link Dialog */}
      {showLinkDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Link</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="linkUrl" className="block text-sm font-medium text-gray-700 mb-2">
                  Link URL
                </label>
                <input
                  id="linkUrl"
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  autoFocus
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowLinkDialog(false);
                    setLinkUrl('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                {editor.isActive('link') && (
                  <button
                    type="button"
                    onClick={() => {
                      if (toolbarDisabled) return;
                      editor.chain().focus().extendMarkRange('link').unsetLink().run();
                      setShowLinkDialog(false);
                      setLinkUrl('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    Remove Link
                  </button>
                )}
                <button
                  type="button"
                  onClick={setLink}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                >
                  {linkUrl.trim() ? 'Set Link' : 'Remove Link'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  icon: React.ReactNode;
  tooltip: string;
  shortcut?: string;
  tabIndex?: number;
}

const ToolbarButton = React.forwardRef<HTMLButtonElement, ToolbarButtonProps>(({
  onClick,
  active = false,
  disabled = false,
  icon,
  tooltip,
  shortcut,
  tabIndex
}, ref) => {
  const fullTooltip = shortcut ? `${tooltip} (${shortcut})` : tooltip;

  return (
    <button
      type="button"
      ref={ref}
      onClick={onClick}
      disabled={disabled}
      tabIndex={tabIndex}
      aria-pressed={active}
      className={`
        relative p-2 rounded-lg transition-all duration-200 transform hover:scale-105
        ${active
          ? 'bg-purple-100 text-purple-700 shadow-sm border border-purple-200'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }
        ${disabled
          ? 'opacity-40 cursor-not-allowed hover:scale-100'
          : 'cursor-pointer'
        }
        focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1
      `}
      title={fullTooltip}
      aria-label={tooltip}
    >
      {icon}
      {active && (
        <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-purple-600 rounded-full"></div>
      )}
    </button>
  );
});

ToolbarButton.displayName = 'ToolbarButton';

const ToolbarDivider: React.FC = () => {
  return <div className="h-6 w-px bg-gray-300 mx-1 hidden sm:block"></div>;
};
